import {
  Fragment,
  type MouseEvent,
  type ReactNode,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import { useTranslation } from "react-i18next";

import { type LucideIcon, MoreHorizontalIcon } from "lucide-react";

import { styled } from "simplydo/react/styled";
import {
  Button,
  ButtonLink,
  type ButtonProps,
  type ButtonVariant,
} from "simplydo/react/ui/button";
import {
  Dropdown,
  DropdownDivider,
  DropdownItem,
  DropdownItemLink,
  DropdownMenu,
} from "simplydo/react/ui/dropdown";
import { Icon } from "simplydo/react/ui/icon";

/**
 * One control on the right of a toolbar. It shows in the toolbar while there
 * is room and moves into the ⋯ menu when there isn't, so the toolbar never
 * wraps.
 */
export type ToolbarItem = {
  key: string;
  /** Items of a group sit together, with a divider between groups. */
  group: string;
  /**
   * - `pinned`: always in the toolbar, e.g. the page's main action.
   * - `high`, `default`, `low`: in the toolbar while they fit. When space
   *   runs out, `low` items move into the menu first, and within a tier the
   *   later item moves first, so list items in order of importance.
   * - `menu`: always in the ⋯ menu.
   */
  tier: "pinned" | "high" | "default" | "low" | "menu";
  label: ReactNode;
  icon?: LucideIcon;
  /** In the toolbar, shows only the icon, with the label as its tooltip. */
  iconOnly?: boolean;
  onClick?: (event: MouseEvent<HTMLElement>) => void;
  to?: string;
  /** Opens in a new tab. */
  href?: string;
  disabled?: boolean;
  loading?: boolean;
  tooltip?: ReactNode;
  variant?: ButtonVariant;
  color?: ButtonProps["color"];
  /** A custom control for the toolbar, in place of a button. */
  render?: () => ReactNode;
  /** A custom menu entry. `false` hides the item instead of listing it. */
  menu?: false | (() => ReactNode);
};

const MORE = "__more";
const GAP = 6;
const RING = 3;
const TIER_ORDER = { low: 0, default: 1, high: 2, pinned: 3, menu: 3 };

const Row = styled.div`
  display: flex;
  flex: 1 1 0;
  min-width: 0;
  align-items: center;
  justify-content: flex-end;
  gap: ${GAP}px;
  overflow: hidden;
  /* Keeps focus rings clear of the clipping. */
  padding: ${RING}px;
  margin: -${RING}px;

  > [data-toolbar-item] {
    display: inline-flex;
    align-items: center;
    flex: none;
  }

  > [data-toolbar-item] > * {
    margin: 0 !important;
  }
`;

const Divider = styled.span`
  flex: none;
  width: 1px;
  height: 24px;
  background: #e4e7ec;
`;

function measureRow(
  shown: ToolbarItem[],
  hasMenu: boolean,
  widths: Map<string, number>,
) {
  const groupChanges = shown.filter(
    (item, index) => index > 0 && shown[index - 1]!.group !== item.group,
  ).length;
  const dividers = groupChanges + (hasMenu && shown.length ? 1 : 0);
  const count = shown.length + dividers + (hasMenu ? 1 : 0);
  return (
    shown.reduce((sum, item) => sum + (widths.get(item.key) ?? 0), 0) +
    (hasMenu ? (widths.get(MORE) ?? 28) : 0) +
    dividers +
    Math.max(count - 1, 0) * GAP
  );
}

function overflowingKeys(
  items: ToolbarItem[],
  widths: Map<string, number>,
  available: number,
) {
  const inline = items.filter((item) => item.tier !== "menu");
  const hidden = new Set<string>();
  const fits = () =>
    measureRow(
      inline.filter((item) => !hidden.has(item.key)),
      items.some(
        (item) =>
          item.tier === "menu" || (hidden.has(item.key) && item.menu !== false),
      ),
      widths,
    ) <= available;

  const candidates = inline
    .filter((item) => item.tier !== "pinned")
    .reverse()
    .sort((a, b) => TIER_ORDER[a.tier] - TIER_ORDER[b.tier]);
  for (const item of candidates) {
    if (fits()) break;
    hidden.add(item.key);
  }
  return hidden;
}

function InlineItem({ item }: { item: ToolbarItem }) {
  if (item.render) return item.render();
  const props = {
    size: "small" as const,
    variant: item.variant ?? "outline",
    color: item.color,
    disabled: item.disabled,
    loading: item.loading,
    tooltip: item.tooltip ?? (item.iconOnly ? item.label : undefined),
    "aria-label":
      item.iconOnly && typeof item.label === "string" ? item.label : undefined,
  };
  const icon = item.icon ? <Icon icon={item.icon} /> : null;
  const label = item.iconOnly ? null : item.label;
  if (item.to) {
    return (
      <ButtonLink {...props} to={item.to}>
        {icon}
        {label}
      </ButtonLink>
    );
  }
  return (
    <Button {...props} onClick={item.onClick}>
      {icon}
      {label}
    </Button>
  );
}

function MenuEntry({ item }: { item: ToolbarItem }) {
  if (item.menu) return item.menu();
  const icon = item.icon ? <Icon icon={item.icon} /> : null;
  if (item.to || item.href) {
    return (
      <DropdownItemLink
        to={item.to}
        href={item.href}
        target={item.href ? "_blank" : undefined}
        rel={item.href ? "noopener noreferrer" : undefined}
        disabled={item.disabled}
      >
        {icon}
        {item.label}
      </DropdownItemLink>
    );
  }
  return (
    <DropdownItem
      disabled={item.disabled || item.loading}
      onClick={(event) => item.onClick?.(event)}
    >
      {icon}
      {item.label}
    </DropdownItem>
  );
}

/**
 * The right of a toolbar: its items in one row, with those that don't fit
 * in the ⋯ menu at its start.
 */
export function ToolbarItems({ items }: { items: ToolbarItem[] }) {
  const { t } = useTranslation();
  const row = useRef<HTMLDivElement>(null);
  const widths = useRef(new Map<string, number>());
  const [hidden, setHidden] = useState<Set<string>>(() => new Set());
  const [rowWidth, setRowWidth] = useState(0);

  useLayoutEffect(() => {
    const element = row.current;
    if (!element) return;
    const observer = new ResizeObserver(([entry]) =>
      setRowWidth(entry?.contentRect.width ?? 0),
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  // Items are measured while shown, so a moved item comes back once its last
  // known width fits again.
  useLayoutEffect(() => {
    const element = row.current;
    if (!element) return;
    for (const child of element.querySelectorAll<HTMLElement>(
      ":scope > [data-toolbar-item]",
    )) {
      widths.current.set(
        child.dataset.toolbarItem!,
        child.getBoundingClientRect().width,
      );
    }
    const next = overflowingKeys(
      items,
      widths.current,
      element.clientWidth - 2 * RING,
    );
    if (next.size !== hidden.size || [...next].some((key) => !hidden.has(key)))
      setHidden(next);
  }, [items, hidden, rowWidth]);

  const shown = items.filter(
    (item) => item.tier !== "menu" && !hidden.has(item.key),
  );
  const listed = [
    ...items.filter(
      (item) =>
        item.tier !== "menu" && hidden.has(item.key) && item.menu !== false,
    ),
    ...items.filter((item) => item.tier === "menu"),
  ];

  return (
    <Row ref={row}>
      {listed.length ? (
        <span data-toolbar-item={MORE}>
          <Dropdown
            direction="left"
            trigger={
              <Button
                size="small"
                variant="ghost"
                aria-label={t("generic.toolbar.moreActions")}
              >
                <Icon icon={MoreHorizontalIcon} />
              </Button>
            }
          >
            <DropdownMenu>
              {listed.map((item, index) => (
                <Fragment key={item.key}>
                  {index > 0 && listed[index - 1]!.group !== item.group ? (
                    <DropdownDivider />
                  ) : null}
                  <MenuEntry item={item} />
                </Fragment>
              ))}
            </DropdownMenu>
          </Dropdown>
        </span>
      ) : null}
      {shown.map((item, index) => (
        <Fragment key={item.key}>
          {(
            index > 0 ? shown[index - 1]!.group !== item.group : listed.length
          ) ? (
            <Divider aria-hidden />
          ) : null}
          <span data-toolbar-item={item.key}>
            <InlineItem item={item} />
          </span>
        </Fragment>
      ))}
    </Row>
  );
}
